import { ChangeEvent, PropsWithChildren, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  FieldValues,
  FieldPath,
  UseControllerProps,
  ControllerRenderProps,
  useFormState,
  useWatch,
  useFormContext,
} from 'react-hook-form';
import FormField from '@ingka/form-field';
import ComboBox, { MenuItem } from '@ingka/combobox';
import Search from '@ingka/search';

import { getter } from '@/lib/utils/object';
import { extractErrorMessage } from '../error-message';

import styles from './index.module.css';
import { PillComboItem, PillComboWrapper } from './pill';

export type ComboItem = {
  value: string;
  i18nKey: string;
  disabled?: boolean;
  quantityLabel?: number;
};

export type ControlledComboProps<T extends FieldValues> = {
  className?: string;
  placeholder?: string;
  // Form
  name: FieldPath<T>;
  rules?: UseControllerProps<T>['rules'];
  forcedError?: any;
  // Ingka form field props
  readOnly?: boolean;
  label: React.ReactNode;
  items: ComboItem[];
  shouldValidate?: boolean;
  successMessage?: string;
  multiple?: boolean;
  searchInDropdown?: boolean;
  showPills?: boolean;
  sorted?: boolean;
};

// As the combobox Skapa component expects the value to be in a specific format
// string (without special characters), we need to convert the values from the form
// to that format and vice versa.
//
// We use hex encoding to ensure that the value is a string with only alphanumeric characters
// and can be safely used as an id in the combobox.
const PREFIX = 'HEX';
const serializeValue = (value: string) =>
  PREFIX +
  Array.from(new TextEncoder().encode(value))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
const normalizeValue = (value: string) =>
  new TextDecoder().decode(
    new Uint8Array(
      value
        .replace(PREFIX, '')
        .match(/.{1,2}/g)
        ?.map((byte) => parseInt(byte, 16)) ?? []
    )
  );
const cleanValues = (values: string[]) => values.map(normalizeValue);
const parseComboValue = (value?: string | string[]) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(serializeValue);
  return [serializeValue(value)];
};

export function ControlledCombo<T extends FieldValues>(
  props: PropsWithChildren<ControlledComboProps<T>>
) {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const {
    name,
    rules,
    shouldValidate = true,
    label,
    successMessage,
    forcedError,
    items,
    multiple,
    searchInDropdown = false,
    placeholder = '',
    readOnly,
    showPills = true,
    sorted = false,
  } = props;
  const [searchString, setSearchString] = useState('');
  const values: string[] = useWatch({ name });
  const parseComboTitle = (field: ControllerRenderProps<any, any>) =>
    (Array.isArray(field.value) ? field.value : [field.value])
      ?.map((value: string) => {
        const item = items.find((item) => item.value === value);
        return item ? t(item.i18nKey) : '';
      })
      .join(', ');
  const sortedItems = useMemo(() => {
    return sorted
      ? [...items].sort((a, b) => t(a.i18nKey).localeCompare(t(b.i18nKey)))
      : items;
  }, [items, sorted, t]);
  const visibleItems = useMemo(() => {
    const query = searchString.trim().toLocaleLowerCase();
    return sortedItems.filter(({ i18nKey }) =>
      t(i18nKey).toLocaleLowerCase().includes(query)
    );
  }, [searchString, sortedItems, t]);
  const { errors } = useFormState({ name });
  const error = forcedError || getter(errors, name);
  const errorMsgArgs = error && extractErrorMessage(error);
  const errorMsg = error && t(...errorMsgArgs);

  // Event handlers
  const handleCleanValue = (onChange: (value: string | string[]) => void) => {
    onChange(multiple ? [] : '');
  };

  const handleListBoxChange = (
    values: string[],
    onChange: (value: string | string[] | undefined) => void
  ) => {
    const cleanedValues = cleanValues(values);
    onChange(multiple ? cleanedValues : cleanedValues[0]);
  };

  return (
    <div className={`${props.className} mb-2`}>
      <Controller
        name={name}
        rules={rules}
        render={({ field }) => (
          <FormField
            style={{ marginBottom: 8 }}
            fieldHelper={{
              msg: shouldValidate ? successMessage : '',
              type: 'error',
            }}
            validation={{
              msg: errorMsg,
              type: 'error',
            }}
            valid={!error}
            shouldValidate={!!shouldValidate}
          >
            <ComboBox
              {...field}
              value={parseComboValue(field.value)}
              multiple={multiple}
              id={name}
              labelId={name}
              placeholder={placeholder}
              label={label}
              listboxId={`${name}-listbox`}
              disabled={readOnly}
              onClearClicked={() => handleCleanValue(field.onChange)}
              onListBoxChange={(values: string[]) =>
                handleListBoxChange(values, field.onChange)
              }
              className={`${styles.input} ${
                searchInDropdown ? styles.withSearch : ''
              }`}
              customSelectionTitle={() => parseComboTitle(field)}
            >
              {searchInDropdown && (
                <div
                  className={styles.search}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <Search
                    data-testid={`${name}-search`}
                    ariaLabel={t('label.search')}
                    clearBtnText={t('aria.clearSearch')}
                    id={`${name}-search`}
                    value={searchString}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setSearchString(event.target.value)
                    }
                    onClear={() => setSearchString('')}
                    submitBtnText={t('aria.submit')}
                  />
                </div>
              )}
              {visibleItems.map(
                ({ value, i18nKey, disabled, quantityLabel }) => (
                  <MenuItem
                    id={serializeValue(value)}
                    disabled={disabled}
                    key={value}
                    title={t(i18nKey)}
                    quantityLabel={quantityLabel}
                  />
                )
              )}
            </ComboBox>
          </FormField>
        )}
      />

      {showPills && (
        <PillComboWrapper>
          {sortedItems
            .filter(({ value }) => values.includes(value))
            .map(({ value, i18nKey }) => (
              <PillComboItem
                key={value}
                title={t(i18nKey)}
                onRemove={() => {
                  const newValues = values.filter((v) => v !== value);
                  setValue(name, newValues as any, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              />
            ))}
        </PillComboWrapper>
      )}
    </div>
  );
}

export default ControlledCombo;
